import { Button } from '@mui/material';
import type { CodeDeliveryDetails } from 'amazon-cognito-identity-js';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import axios from 'axios';
import classNames from 'classnames';
import clsx from 'clsx';
import { StatusCodes } from 'http-status-codes';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import customConfirmAlert from '../../../components/ConfirmAlert';
import TextField from '../../../components/Inputs/TextField';
import useMixpanel from '../../../hooks/useMixpanel';
import { useLoginService } from '../../../services/loginService/index';
import type { IForgotPassword } from '../../../services/loginService/store';
import { forgotPasswordState } from '../../../services/loginService/store';
import { getPortalRuntimeConfig } from '../../../services/runtimeConfig';
import { getErrorMessage } from '../../../utils/errorUtil';
import rootStyles from '../index.module.scss';
import formStyles from '../LoginContent/index.module.scss';
import styles from './index.module.scss';
import { SupportLink } from './SupportLink';

type CodeChallengeContext = {
  user: CognitoUser;
  username: string;
  codeDeliveryDetails: CodeDeliveryDetails;
};

const isVerificationCodeError = (err: Error) =>
  err.name === 'CodeMismatchException' || err.name === 'ExpiredCodeException';

function ForgotPasswordForm() {
  const { state, login } = useLoginService();
  const { cognito } = getPortalRuntimeConfig();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<{ username: string }>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { track, trackClick, trackFail, trackSuccess } = useMixpanel({
    initData: { component: 'forgot_password' },
  });
  const resetCredentialRef = useRef<{ username: string; password: string } | null>(null);
  const isConfirmInFlightRef = useRef(false);

  const updateForgotPassword = (forgotPassword: IForgotPassword) => {
    state.setForgotPassword({
      ...state.forgotPassword,
      ...forgotPassword,
    });
  };

  const onForgotPasswordConfirm = ({
    body,
    subText,
  }: {
    body: string;
    subText?: string | string[];
  }) => {
    customConfirmAlert({
      title: 'Forgot your password?',
      body,
      subText,
      label: 'Go back to login',
      onClick: () => {
        state.setForgotPassword({
          value: false,
        });
      },
      classes: {
        overlay: styles.forgotPasswordOverlay,
        primary: styles.loginBtn,
        body: styles.body,
        secondary: styles.hidden,
        closeBtn: styles.hidden,
        subText: styles.subText,
      },
    });
  };

  const onSubmit = async ({ username }: { username: string }) => {
    trackClick('enter_username');
    resetCredentialRef.current = null;
    isConfirmInFlightRef.current = false;

    try {
      await axios.put('/api/camel/auth/forgot_password_flow', {
        email: username,
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === StatusCodes.CONFLICT) {
        onForgotPasswordConfirm({
          body: `We have sent you an email with a temporary password.`,
          subText: 'Please follow the further instruction within the received email.',
        });
      } else if (status === StatusCodes.BAD_REQUEST) {
        onForgotPasswordConfirm({
          body: `We've emailed you a login link.`,
          subText: [
            'Please check your inbox and follow the instructions provided.',
            `Alternatively, you can sign in using your Google account for quicker access.`,
          ],
        });
      } else {
        state.setErrorMessager(getErrorMessage(e));
      }
      return;
    }

    const isCodeSent = await requestVerificationCode(username);
    if (isCodeSent) {
      state.setForgotPassword({
        value: false,
      });
    }
  };

  const requestVerificationCode = (username: string) =>
    new Promise<boolean>((resolve) => {
      const user = new CognitoUser({
        Username: username.toLowerCase(),
        Pool: new CognitoUserPool({
          UserPoolId: cognito.userPoolId,
          ClientId: cognito.clientId,
        }),
      });

      user.forgotPassword({
        onSuccess() {
          resolve(true);
        },
        onFailure(err) {
          state.setErrorMessager(err.message);
          trackFail('cognito_forget_password', { err });
          resolve(false);
        },
        inputVerificationCode(data: { CodeDeliveryDetails: CodeDeliveryDetails }) {
          track('verification_required');
          startCodeChallenge({ user, username, codeDeliveryDetails: data.CodeDeliveryDetails });
          resolve(true);
        },
      });
    });

  const startCodeChallenge = ({ user, username, codeDeliveryDetails }: CodeChallengeContext) => {
    state.setChallenge({
      name: 'SMS_MFA',
      params: {
        CODE_DELIVERY_DELIVERY_MEDIUM:
          codeDeliveryDetails.DeliveryMedium === 'EMAIL' ? 'email' : 'sms',
        CODE_DELIVERY_DESTINATION: codeDeliveryDetails.Destination,
      },
      onFinish(code) {
        startNewPasswordChallenge({ user, username, codeDeliveryDetails, code });
      },
      onResend() {
        requestVerificationCode(username);
      },
    });
  };

  const startNewPasswordChallenge = ({
    user,
    username,
    codeDeliveryDetails,
    code,
  }: CodeChallengeContext & { code: string }) => {
    state.setChallenge({
      name: 'NEW_PASSWORD_REQUIRED',
      onFinish(password) {
        if (isConfirmInFlightRef.current || resetCredentialRef.current) {
          return;
        }
        isConfirmInFlightRef.current = true;
        const mixpanelAction = 'cognito_confirm_new_password';
        track(mixpanelAction);
        user.confirmPassword(code, password, {
          onSuccess: () => {
            isConfirmInFlightRef.current = false;
            trackSuccess(mixpanelAction);
            resetCredentialRef.current = { username, password };
            updateForgotPassword({
              state: forgotPasswordState.confirmed,
            });
          },
          onFailure(err) {
            isConfirmInFlightRef.current = false;
            trackFail(mixpanelAction);
            if (resetCredentialRef.current) {
              return;
            }
            if (isVerificationCodeError(err)) {
              startCodeChallenge({ user, username, codeDeliveryDetails });
            }
            state.setErrorMessager(err.message);
          },
        });
      },
      onLogin() {
        if (resetCredentialRef.current) {
          login(resetCredentialRef.current);
        }
      },
    });
  };

  const onValidateForm = ({ username }: { username: string }) => {
    trackClick('enter_username');
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username)) {
      return onSubmit({ username });
    }
    updateForgotPassword({
      state: forgotPasswordState.identification,
    });
  };

  useEffect(() => {
    reset();
  }, [state.forgotPassword]);

  return (
    <div
      className={classNames(formStyles.root, rootStyles.boxContainer, 'w-full', {
        [rootStyles.hide]:
          !state.forgotPassword.value ||
          state.loginWithSso.value ||
          state.forgotPassword.state ||
          state.isWelcome,
      })}
    >
      <div className={styles.content}>
        <span className={rootStyles.title}>Forgot your password? </span>
        <div className="font-normal text-neutrals-n-400 text-center text-base w-full leading-snug ">
          To reset your password, enter the Email address or username associated with your OneStep
          account.
        </div>
      </div>
      <form onSubmit={handleSubmit(onValidateForm)}>
        <TextField
          placeholder="Email / Username"
          wrapperClassName={formStyles.inputWrapper}
          error={!!errors?.username}
          helperText={errors?.username?.message}
          inputClassName={formStyles.inputField}
          hideErrorIcon
          variant="outlined"
          InputLabelProps={{
            classes: {
              shrink: formStyles.shrink,
            },
          }}
          {...register('username', {
            required: 'Please enter user name',
          })}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isSubmitting}
          className={clsx(formStyles.submitBtn, 'rounded-4')}
        >
          Continue
        </Button>
        {state.errorMessage ? <div className={formStyles.error}>{state.errorMessage}</div> : null}
      </form>
      <SupportLink />
    </div>
  );
}
export default observer(ForgotPasswordForm);
